import {
  Component,
  Inject,
  OnInit,
  ChangeDetectionStrategy
} from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { UtilsService } from '../../../shared/utils.service';
import { ApiRequestService } from '../../../shared/api-request.service';
import { AppService } from '../../../app.service';
import { RbacPermission } from '../../../interfaces/rbac-permission.interface';
import { getRbacFeatureDescription } from '../rbac-feature-descriptions';

interface PermissionsSelectorFeature {
  rbac_permissions: RbacPermission[];
}

interface PermissionsSelectorCategory {
  name: string;
  icon: string;
  expanded: boolean;
  features: { [key: string]: PermissionsSelectorFeature };
}

/**
 * A dialog for selecting RBAC permissions. It is a pure picker: it never mutates a role, it simply
 * returns the selected permission ids so a caller can use them (currently the RBAC roles filter).
 *
 * The catalogue is grouped the same way as the RBAC roles editor (category, then feature, then an
 * action column per permission) so the selection UI is familiar, but the quick access shortcuts and
 * the view-is-compulsory-for-edit rules are deliberately absent: a filter selection carries no
 * permission semantics, so any combination is valid.
 */
@Component({
  selector: 'app-rbac-permissions-selector',
  templateUrl: './rbac-permissions-selector.component.html',
  styleUrls: ['./rbac-permissions-selector.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false
})
export class RbacPermissionsSelectorComponent implements OnInit {
  // The full (plan filtered) permission catalogue.
  rbac_permissions: RbacPermission[] = [];

  // Categories built from the catalogue, and the search filtered view of them.
  categories: { [key: string]: PermissionsSelectorCategory } = {};
  filtered_categories: { [key: string]: PermissionsSelectorCategory } = {};

  // Every distinct action across the catalogue. Drives the checkbox columns.
  availableActions: string[] = [];

  // The feature search text.
  searchText: string = '';

  // Tracks the catalogue request so the template can show a loading state.
  isLoading: boolean = true;
  // What the selection is for: 'filter' (default, the RBAC roles filter) or 'template' (the super
  // admin partner permission template). Only the title and the checkbox tooltips differ.
  purpose: 'filter' | 'template' = 'filter';

  // Icon mapping for common category name patterns.
  private categoryIconMap: { [key: string]: string } = {
    account: 'business',
    site: 'location_on',
    contractor: 'engineering',
    employee: 'people',
    safety: 'health_and_safety',
    register: 'list_alt',
    template: 'description',
    report: 'assessment',
    task: 'task_alt',
    form: 'dynamic_form',
    message: 'mail',
    client: 'handshake',
    networked: 'hub'
  };

  constructor(
    public utils: UtilsService,
    public app: AppService,
    private api: ApiRequestService,
    @Inject(MAT_DIALOG_DATA) public data: any,
    private dialogRef: MatDialogRef<RbacPermissionsSelectorComponent>
  ) {}

  ngOnInit(): void {
    this.purpose = this.data && this.data['purpose'] === 'template' ? 'template' : 'filter';
    this.getRBACPermissions();
  }

  /**
   * Retrieves the RBAC permission catalogue from the API and applies the incoming selection.
   *
   * @returns {void}
   */
  private getRBACPermissions(): void {
    this.api
      .makeRequest('get', 'v2/rbac-permissions')
      .then(
        (response: RbacPermission[]): void => {
          // Reduce the catalogue to the permissions the active account's plan unlocks. A permission
          // the account cannot hold can never appear on one of its roles, so offering it as a filter
          // would only ever return an empty list.
          this.rbac_permissions = (response || []).filter(
            (rbac_permission: RbacPermission): boolean => {
              const slug: string = (rbac_permission.permission || '').split(
                ':'
              )[0];
              return this.app.planCanRbacFeature(slug);
            }
          );

          // Apply the selection passed in by the caller.
          const selected: number[] =
            this.data && this.data['selected'] ? this.data['selected'] : [];
          this.rbac_permissions.forEach(
            (rbac_permission: RbacPermission): void => {
              rbac_permission.checked =
                selected.indexOf(rbac_permission.id) > -1;
            }
          );

          this.buildCategories();
        }
      )
      .catch(
        (errorResponse: any): void => {
          this.utils.handleAPIErrors(errorResponse);
        }
      )
      .finally(
        (): void => {
          this.isLoading = false;
        }
      );
  }

  /**
   * Groups the catalogue into categories and features. Feature labels are stored as
   * "Category: Feature", so the part before the colon becomes the category.
   *
   * @returns {void}
   */
  private buildCategories(): void {
    this.categories = {};

    this.rbac_permissions.forEach(
      (rbac_permission: RbacPermission): void => {
        const featureLabel: string = rbac_permission.feature || 'Other';
        const categoryName: string = featureLabel.includes(':')
          ? featureLabel.split(':')[0].trim()
          : featureLabel;
        const featureName: string = featureLabel.includes(':')
          ? featureLabel.split(':')[1].trim()
          : featureLabel;

        if (!this.categories[categoryName]) {
          this.categories[categoryName] = {
            name: categoryName,
            icon: this.getIconForCategory(categoryName),
            expanded: false,
            features: {}
          };
        }

        if (!this.categories[categoryName].features[featureName]) {
          this.categories[categoryName].features[featureName] = {
            rbac_permissions: []
          };
        }

        this.categories[categoryName].features[
          featureName
        ].rbac_permissions.push(rbac_permission);
      }
    );

    // Expand the categories that already contain a selection so the incoming filter is visible.
    Object.keys(this.categories).forEach(
      (categoryKey: string): void => {
        this.categories[categoryKey].expanded =
          this.getCategoryCount(categoryKey).selected > 0;
      }
    );

    this.availableActions = Array.from(
      new Set(
        this.rbac_permissions.map(
          (rbac_permission: RbacPermission): string => rbac_permission.action
        )
      )
    ).sort((a: string, b: string): number => a.localeCompare(b));

    this.onFilterFeatures();
  }

  /**
   * Gets an appropriate icon for a category based on its name.
   *
   * @param {string} categoryName - The category name.
   * @returns {string} The Material icon name.
   */
  private getIconForCategory(categoryName: string): string {
    const lowerName: string = categoryName.toLowerCase();
    for (const [key, icon] of Object.entries(this.categoryIconMap)) {
      if (lowerName.includes(key)) {
        return icon;
      }
    }
    return 'folder_open';
  }

  /**
   * Rebuilds the filtered category view from the search text. The filter keeps the permission object
   * references intact so a checkbox toggled while searching still updates the real selection.
   *
   * @returns {void}
   */
  onFilterFeatures(): void {
    const term: string = (this.searchText || '').trim().toLowerCase();

    if (!term) {
      this.filtered_categories = this.categories;
      return;
    }

    this.filtered_categories = {};

    Object.keys(this.categories).forEach(
      (categoryKey: string): void => {
        const category: PermissionsSelectorCategory = this.categories[
          categoryKey
        ];
        const categoryMatches: boolean = categoryKey
          .toLowerCase()
          .includes(term);
        const features: { [key: string]: PermissionsSelectorFeature } = {};

        Object.keys(category.features).forEach(
          (featureKey: string): void => {
            const featureMatches: boolean =
              categoryMatches ||
              featureKey.toLowerCase().includes(term) ||
              category.features[featureKey].rbac_permissions.some(
                (p: RbacPermission): boolean => {
                  return (p.permission || '').toLowerCase().includes(term);
                }
              );

            if (featureMatches) {
              features[featureKey] = category.features[featureKey];
            }
          }
        );

        if (Object.keys(features).length > 0) {
          this.filtered_categories[categoryKey] = {
            ...category,
            expanded: true,
            features: features
          };
        }
      }
    );
  }

  /**
   * Clears the feature search.
   *
   * @returns {void}
   */
  clearSearch(): void {
    this.searchText = '';
    this.onFilterFeatures();
  }

  /**
   * The category keys of the filtered view, sorted alphabetically.
   *
   * @returns {string[]}
   */
  getSortedCategoryKeys(): string[] {
    return Object.keys(this.filtered_categories).sort(
      (a: string, b: string): number => a.localeCompare(b)
    );
  }

  /**
   * Finds the permission for a given action within a feature. Returns undefined when the feature
   * does not define that action (not every feature has all four).
   *
   * @param {RbacPermission[]} permissions - The feature permissions.
   * @param {string} action - The action to look up.
   * @returns {RbacPermission | undefined}
   */
  getPermissionForAction(
    permissions: RbacPermission[],
    action: string
  ): RbacPermission | undefined {
    return permissions.find(
      (p: RbacPermission): boolean => p.action === action
    );
  }

  /**
   * Returns the tooltip text describing what a feature covers, or null when none is defined
   * (the template then shows no info icon). Looked up by the feature slug, not the display label.
   *
   * @param {RbacPermission[]} featurePermissions - All permissions of one feature row.
   * @returns {string | null}
   */
  getFeatureDescription(featurePermissions: RbacPermission[]): string | null {
    if ( !featurePermissions || featurePermissions.length === 0 ) {
      return null;
    }
    return getRbacFeatureDescription(featurePermissions[0].permission);
  }

  /**
   * The Material icon for an action.
   *
   * @param {string} action - The action name.
   * @returns {string}
   */
  getIconForAction(action: string): string {
    switch (action) {
      case 'view':
        return 'visibility';
      case 'create':
        return 'add_circle_outline';
      case 'edit':
        return 'edit';
      case 'delete':
        return 'delete_outline';
      default:
        return 'check_circle_outline';
    }
  }

  /**
   * The selected and total permission counts for a category.
   *
   * @param {string} categoryKey - The category key.
   * @returns {{ selected: number, total: number }}
   */
  getCategoryCount(categoryKey: string): { selected: number; total: number } {
    const category: PermissionsSelectorCategory = this.categories[categoryKey];
    if (!category) {
      return { selected: 0, total: 0 };
    }

    let selected: number = 0;
    let total: number = 0;

    Object.values(category.features).forEach(
      (feature: PermissionsSelectorFeature): void => {
        feature.rbac_permissions.forEach(
          (p: RbacPermission): void => {
            total++;
            if (p.checked) {
              selected++;
            }
          }
        );
      }
    );

    return { selected: selected, total: total };
  }

  /**
   * Selects every permission of a feature.
   *
   * @param {RbacPermission[]} permissions - The feature permissions.
   * @returns {void}
   */
  onSelectFeature(permissions: RbacPermission[]): void {
    permissions.forEach(
      (p: RbacPermission): void => {
        p.checked = true;
      }
    );
  }

  /**
   * Deselects every permission of a feature.
   *
   * @param {RbacPermission[]} permissions - The feature permissions.
   * @returns {void}
   */
  onDeselectFeature(permissions: RbacPermission[]): void {
    permissions.forEach(
      (p: RbacPermission): void => {
        p.checked = false;
      }
    );
  }

  /**
   * The total number of selected permissions.
   *
   * @returns {number}
   */
  getSelectedCount(): number {
    return this.rbac_permissions.filter(
      (p: RbacPermission): boolean => !!p.checked
    ).length;
  }

  /**
   * Clears the whole selection.
   *
   * @returns {void}
   */
  onClearSelection(): void {
    this.rbac_permissions.forEach(
      (p: RbacPermission): void => {
        p.checked = false;
      }
    );
  }

  /**
   * The tooltip of a permission checkbox, worded for the dialog's purpose.
   *
   * @param {RbacPermission} permission - The permission.
   * @returns {string}
   */
  getPermissionTooltip(permission: RbacPermission): string {
    return this.purpose === 'template'
      ? 'Include the ' + permission.permission + ' permission in the template.'
      : 'Filter by the ' + permission.permission + ' permission.';
  }

  /**
   * Closes the dialog with the selected permission ids.
   *
   * @returns {void}
   */
  onSaveAndClose(): void {
    this.dialogRef.close(
      this.rbac_permissions
        .filter((p: RbacPermission): boolean => !!p.checked)
        .map((p: RbacPermission): number => p.id)
    );
  }

  /**
   * Closes the dialog without returning a selection.
   *
   * @returns {void}
   */
  onClose(): void {
    this.dialogRef.close();
  }
}
